import { useEffect, useMemo, useState } from 'react';
import { fmtMemberId } from '@lib/member-id';
import { StageDots } from '@components/StageDots';
import { HeldHeight } from '@components/HeldHeight';
import { PhoneActions } from '@components/PhoneActions';
import { CareGrid, useGridRows } from '@components/grid/CareGrid';
import { useGridView, type GridColumn, type GridView } from '@components/grid/useGridView';
import type { Facet, SlicerDef, SortKey as GridSortKey } from '@lib/grid';
import { replyOfferLabel } from '@lib/reply-groups';
import { isReceiveOnly } from '@lib/receive-only';
import { STAGED_WORDING, pendingCountOf, pendingOpsOf } from '@lib/staged-presses';
import { clientDisplayName } from '@lib/clients';
import { claimHolderName } from '@lib/claim-holder';
import { threadContact, threadMainLine, threadSubLine } from '@components/ThreadTable';
import { MatchCell, SuggestionPill } from '@components/SuggestionPill';
import { MATCH_GROUP_WORDING, eventMatchState, matchSortValue, unconfirmed } from '@lib/match-suggest';
import { ContactCell, LINE2_LEAD, MessageLine, ROWS_TABLE_CLASS, SuggestedCell, TwoLineCell, claimedByColumn, rowBand, rowNoColumn, tickColumn } from '@components/QueueRowCells';
import {
  ACTION_META,
  CAMPAIGN_META,
  SUGGESTED_META,
  CAMPAIGN_SOURCE_WORDING,
  CHANNEL_META,
  NO_CAMPAIGN_WORDING,
  OPTED_OUT_ROW_STYLE,
  OPTED_OUT_WORDING,
  SOURCE_META,
  STATUS_META,
  campaignLabelOf,
  fmtMinutes,
  fmtPhone,
  fmtTs,
  liveTatMinutes,
  stageIndex,
  statusLabel,
  type ActionType,
  type EventRow,
  type ThreadRow,
  REPLY_CLASS_META,
} from '@lib/care-model';

/**
 * QueueTable - the ClariCommand `.dt` event table shared by the Inbound (flat), Calling, Live call,
 * Consent-chase and Final Review queues. PHI-light: shows the matched member's display name +
 * stripped id, never the full snapshot (that loads on row-open in the drawer).
 *
 * rev4: adds the Source pill column (Member / Intake / Print Station / Portal, referral reason as
 * the tooltip) and the resolution-TAT column (d/h/m: live-ticking for open rows, frozen at
 * sentToReviewTs once sent - William ruling #11). Optional checkbox selection powers the Final
 * Review "Approve (N)" batch.
 *
 * ROW 356 (approval 169): the table renders through the shared grid, so every queue has the
 * Columns tick list, drag-to-resize, click and Shift-click sorting over the WHOLE list, slicers and
 * Expand, each remembered per operator per screen. The page calls `useQueueGrid` (it needs the rows
 * on screen: Final Review's "Select all" must never tick a row a slicer hid) and hands the result
 * here. The sort values are the ones Final Review's comparator has used since 2026-07-08: labels as
 * shown, blanks last in both directions, TAT on the same live value the cell shows.
 */

/** The slicers a queue may offer. The page's own chips (source, campaign, text replies, worklist)
 *  stay where they are; a queue offers a slicer only for a field no chip already filters. */
export type QueueSlicer =
  | 'channel' | 'source' | 'campaign' | 'status' | 'instruction' | 'actions' | 'match' | 'handledBy' | 'replyClass'
  | 'cohort' | 'cycle' | 'claimedBy';

/**
 * APPROVAL 185: what the Cohort slicer calls a row with nothing there (a reply whose member has no
 * cohort on the roster, or an entry that is not a text reply). Offered as a choice, because a yes with
 * no cohort is the one she cannot match to a kit and must be able to find. (I4, 16 Oct 2026: the Offer
 * slicer and its "No offer" choice are gone.)
 */
export const KIT_SLICER_WORDING = { noCohort: 'No cohort' } as const;

const MATCH_LABEL: Record<string, string> = { matched: 'Matched', ambiguous: 'Ambiguous', unmatched: 'Unmatched' };

function matchState(r: EventRow): string {
  return r.memberId ? 'matched' : r.matchConfidence === 'ambiguous' ? 'ambiguous' : 'unmatched';
}

function actionLabel(a: string): string {
  return ACTION_META[a as ActionType]?.label ?? a;
}

/** The Actions slicer's key prefix for a waiting press (never an action type: action types carry no colon). */
const QUEUED_OP = 'queued:';

const SLICER_DEFS: Record<QueueSlicer, SlicerDef<EventRow>> = {
  channel: { key: 'channel', label: 'Channel', value: (r) => r.channel, labelFor: (v) => CHANNEL_META[v as EventRow['channel']]?.label ?? v },
  source: { key: 'source', label: 'Source', value: (r) => r.source, labelFor: (v) => SOURCE_META[v as EventRow['source']]?.label ?? v },
  campaign: {
    key: 'campaign', label: 'Campaign', value: (r) => r.campaign, blankLabel: NO_CAMPAIGN_WORDING.chip,
    labelFor: (v) => CAMPAIGN_META[v]?.label ?? v,
  },
  status: { key: 'status', label: 'Status', value: (r) => r.status, labelFor: (v) => statusLabel(v as EventRow['status']) },
  instruction: { key: 'instruction', label: 'Instruction', value: (r) => r.instruction, blankLabel: 'None', labelFor: actionLabel },
  // William, 22 Oct 2026: a case with nothing resolved yet counts its waiting presses under their own name, "(queued)".
  actions: {
    key: 'actions', label: 'Actions', blankLabel: 'None',
    value: (r) => (r.actions.length ? r.actions : pendingOpsOf(r).map((w) => `${QUEUED_OP}${w}`)),
    labelFor: (v) => (v.startsWith(QUEUED_OP) ? STAGED_WORDING.queuedOpSlicer(v.slice(QUEUED_OP.length)) : actionLabel(v)),
  },
  match: { key: 'match', label: 'Member', value: matchState, labelFor: (v) => MATCH_LABEL[v] ?? v },
  handledBy: { key: 'handledBy', label: 'Handled by', value: (r) => r.operatorName || r.assignedTo, blankLabel: 'Nobody yet' },
  replyClass: {
    key: 'replyClass', label: 'Text reply', value: (r) => r.replyClass, blankLabel: 'Not a text reply',
    labelFor: (v) => (REPLY_CLASS_META as Record<string, { label: string }>)[v]?.label ?? v,
  },
  // Approval 185: the yeses by kit. The Cohort is the roster's own words, never translated into a kit name
  // (the cohort decides the kit, and the mapping is unconfirmed). I4 (William, 16 Oct 2026 about 10 PM:
  // remove): no Offer slicer on any queue screen.
  cohort: { key: 'cohort', label: 'Cohort', value: (r) => r.replyCohort, blankLabel: KIT_SLICER_WORDING.noCohort },
  // Slice B (his img7): the round a text reply answered.
  cycle: { key: 'cycle', label: 'Cycle', value: (r) => r.cycle ?? '', blankLabel: 'Not a text reply' },
  // 16 Oct 2026 8:52 PM: the Inbound queue's Claimed by column, sliced by the same holder name.
  claimedBy: { key: 'claimedBy', label: 'Claimed by', value: (r) => claimHolderName(r), blankLabel: 'Nobody' },
};

interface ColumnOpts {
  nowMs: number;
  callable: boolean;
  checkedIds: ReadonlySet<string> | undefined;
  onToggleCheck: ((id: string) => void) | undefined;
  /** Approval 187: why a row's tick box is disabled ('' = tickable); the reason is its tooltip. */
  tickBlock?: ((r: EventRow) => string) | undefined;
  /** Approval 187: marks the page hangs on a row, beside its reply pill. */
  rowMarks?: ((r: EventRow) => ReadonlyArray<{ key: string; text: string; pill: string; title: string }>) | undefined;
  /** 16 Oct 2026 8:20 PM: the rows drawn like Group by member (see rowsLookColumns). */
  rowsLook?: boolean | undefined;
  /** Rows look: the rows on screen the header box ticks, and its press. */
  headerIds?: readonly string[] | undefined;
  onToggleAll?: ((ids: string[]) => void) | undefined;
}

/**
 * Who an entry is from, in the grouped view's own terms (api/src/lib/thread-model.ts threadKeyOf and
 * labelFor): the member, else the caller's PCP, else the phone, else the address, so the flat view's
 * Member cell reads exactly as the grouped view's does for the same person.
 */
export function eventWho(r: Pick<EventRow, 'memberId' | 'memberName' | 'pcpNpi' | 'fromPhone' | 'fromAddress' | 'matchConfidence'>): Pick<ThreadRow, 'kind' | 'label' | 'sublabel' | 'matchConfidence'> {
  if (r.memberId) return { kind: 'member', label: r.memberName || 'member', sublabel: r.memberId, matchConfidence: r.matchConfidence };
  if (r.pcpNpi) return { kind: 'pcp', label: 'PCP office', sublabel: `NPI ${r.pcpNpi}`, matchConfidence: r.matchConfidence };
  if (r.fromPhone) return { kind: 'phone', label: r.fromPhone, sublabel: '', matchConfidence: r.matchConfidence };
  if (r.fromAddress) return { kind: 'email', label: r.fromAddress, sublabel: '', matchConfidence: r.matchConfidence };
  return { kind: 'event', label: 'unknown', sublabel: '', matchConfidence: r.matchConfidence };
}

/** The flat row's Member cell, line one (the name, or the phone or address it came from). */
export function eventMainLine(r: Parameters<typeof eventWho>[0]): string {
  return threadMainLine(eventWho(r));
}

/** The flat row's Contact cell: the entry's own phone and email address, whichever it has. */
export function eventContact(r: Pick<EventRow, 'fromPhone' | 'fromAddress'>): { phone: string; email: string } {
  const addr = r.fromAddress ?? '';
  return threadContact({ kind: 'event', fromPhone: r.fromPhone, fromAddress: addr, contactEmail: addr.includes('@') ? addr : '' });
}

const nowrap = { whiteSpace: 'nowrap' } as const;

function queueColumns(o: ColumnOpts): GridColumn<EventRow>[] {
  const tat = (r: EventRow) => r.tatMinutes ?? liveTatMinutes(r.receivedTs, r.sentToReviewTs || undefined, o.nowMs);
  const cols: GridColumn<EventRow>[] = [
    {
      key: 'select', label: '', menuLabel: 'Tick box', locked: true, stopRowClick: true,
      thStyle: { width: 28 },
      tdStyle: { width: 28 },
      render: (r) => {
        const blocked = o.tickBlock?.(r) ?? '';
        return (
          <input
            type="checkbox"
            aria-label={`Select event ${r.eventId}`}
            checked={!blocked && (o.checkedIds?.has(r.eventId) ?? false)}
            disabled={!!blocked}
            title={blocked || undefined}
            onChange={() => o.onToggleCheck?.(r.eventId)}
            onKeyDown={(e) => e.stopPropagation()}
          />
        );
      },
    },
    { key: 'date', label: 'Date / Time', tdStyle: nowrap, sortValue: (r) => Date.parse(r.receivedTs) || null, render: (r) => fmtTs(r.receivedTs) },
    {
      key: 'channel', label: 'Channel', sortValue: (r) => r.channel,
      render: (r) => {
        const chan = CHANNEL_META[r.channel] ?? CHANNEL_META.Unknown;
        return (
          <>
            <span className={`pill ${chan.pill}`.trim()}>{chan.label}</span>
            {r.hasAudio ? <span title="audio"> ♪</span> : null}
            {r.hasTranscript ? <span title="transcript"> ▤</span> : null}
          </>
        );
      },
    },
    {
      key: 'source', label: 'Source', sortValue: (r) => SOURCE_META[r.source]?.label ?? r.source,
      render: (r) => {
        const src = SOURCE_META[r.source] ?? SOURCE_META.member;
        return (
          <>
            <span className={`pill ${src.pill}`.trim()} title={r.referralReason ? `referral: ${r.referralReason}` : undefined}>
              {src.label}
            </span>
            {r.optedOut && (
              // The SECOND carrier of the fact (his row 144): the shade is a colour and dies in
              // greyscale, in print and under a screen reader. This says it in words.
              <span
                className="pill pill-accent"
                title="The permanent opt-out record has this member suppressed. Open the Opt-outs screen to review the communication."
                style={{ marginLeft: 4 }}
              >
                {OPTED_OUT_WORDING.pill}
              </span>
            )}
            {r.replyClass && (
              // Row 232: what the member's text reply was, as the platform classified it.
              <span className={`pill ${REPLY_CLASS_META[r.replyClass].pill}`} title={REPLY_CLASS_META[r.replyClass].help} style={{ marginLeft: 4 }}>
                {REPLY_CLASS_META[r.replyClass].label}
              </span>
            )}
            {o.rowMarks?.(r).map((m) => (
              <span key={m.key} className={`pill ${m.pill}`} title={m.title} style={{ marginLeft: 4 }} data-testid={`row-mark-${m.key}`}>
                {m.text}
              </span>
            ))}
            {r.claireResolved && (
              <span className="pill pill-success" title="Claire finished intake and auto-routed this to review" style={{ marginLeft: 4 }}>
                Claire Auto Resolved
              </span>
            )}
            {r.internal && (
              <span
                className="pill pill-warning"
                title="This number is on the internal-numbers roster (CDX team / test device). It is never matched to a member."
                style={{ marginLeft: 4 }}
              >
                CDX internal
              </span>
            )}
            {r.resubscribedAfterStop && (
              <span
                className="pill pill-success"
                title={`This number texted START on ${fmtTs(r.resubscribedStartTs ?? '')}, after this STOP. The SMS platform has already re-subscribed the member; recording an opt-out here would mark them do-not-solicit permanently.`}
                style={{ marginLeft: 4 }}
              >
                RE-SUBSCRIBED
              </span>
            )}
          </>
        );
      },
    },
    { key: 'client', label: 'Plan', tdStyle: nowrap, sortValue: (r) => r.client, render: (r) => r.client },
    {
      key: 'campaign', label: 'Campaign', tdStyle: nowrap, sortValue: (r) => campaignLabelOf(r),
      render: (r) =>
        r.campaign ? (
          <span
            className={`pill ${CAMPAIGN_META[r.campaign]?.pill ?? ''}`.trim()}
            title={r.campaignSource ? CAMPAIGN_SOURCE_WORDING[r.campaignSource].help : undefined}
          >
            {campaignLabelOf(r)}{r.campaignSource === 'derived-newest' ? ' *' : ''}
          </span>
        ) : (
          // A NAMED STATE, never a dash. A dash reads as "unfilled"; for a member's text
          // there is no campaign to know, and the tooltip is where that is said.
          <span className="muted" title={NO_CAMPAIGN_WORDING.tooltip}>
            {NO_CAMPAIGN_WORDING.cell}
          </span>
        ),
    },
    // Slice B (his img7): the round of the campaign a text reply answered, blank on anything else.
    {
      key: 'cycle', label: 'Cycle', tdStyle: nowrap, sortValue: (r) => r.cycle ?? '',
      title: 'The round of the campaign this text reply answered (Opt-in 01, Opt-in 02)',
      render: (r) => r.cycle || <span className="muted">-</span>,
    },
    // Approval 185 (shown only where the page asks, the Inbound queue with the reply buttons on): the
    // offer the reply answered and the member's cohort, both blank on anything that is not a reply.
    {
      key: 'offer', label: 'Offer', tdStyle: nowrap, sortValue: (r) => replyOfferLabel(r.replyOffer),
      title: 'The offer this text reply answered. The cohort, not the offer, decides the kit.',
      render: (r) => replyOfferLabel(r.replyOffer),
    },
    {
      key: 'cohort', label: 'Cohort', tdStyle: nowrap, sortValue: (r) => r.replyCohort ?? '',
      title: "The member's cohort, in the campaign roster's own words",
      render: (r) => r.replyCohort ?? '',
    },
    {
      key: 'from', label: 'From', tdStyle: nowrap, sortValue: (r) => r.fromPhone || r.fromAddress,
      // W9 on the live line (the office's DECIDED, 11 Oct 2026): the Call link asks the stop check before Teams
      // opens (fail closed); the roster's do-not-solicit flag still blocks it as before. The press names the row's
      // own case and its from-phone line (T-1010-99-CALLBIND); the server reads the number from that case.
      // RECEIVE-ONLY (his word of 3 Oct 2026; T-1010-99, 3:34 PM 11 Oct): this console never calls back a member of
      // a plan with no texting number of its own, so such a row's Call is blocked here as the drawer blocks it, and
      // nothing is asked (the server's 409 receive_only stays for a direct caller).
      render: (r) =>
        r.fromPhone
          ? (o.callable
              ? <PhoneActions phone={r.fromPhone} compact blocked={!!r.doNotSolicit || isReceiveOnly(r.client)} checkBeforeCall={{ eventId: r.eventId, line: 'from' }} />
              : fmtPhone(r.fromPhone))
          : r.fromAddress || <span className="muted">unknown</span>,
    },
    {
      key: 'content', label: 'Content',
      tdStyle: { maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
      sortValue: (r) => `${r.subject} ${r.preview}`.trim(),
      render: (r) => (
        <>
          {r.subject ? <b>{r.subject} </b> : null}
          <span className="muted">{r.preview}</span>
        </>
      ),
    },
    {
      key: 'member', label: 'Member', tdStyle: nowrap, sortValue: (r) => r.memberName || r.memberId,
      render: (r) =>
        r.memberId ? (
          <span>
            {r.memberName || 'member'} <span className="muted">#{fmtMemberId(r.memberId)}</span>
          </span>
        ) : (
          r.matchPending || unconfirmed(r)
            ? <SuggestionPill suggestion={unconfirmed(r)} pending={r.matchPending} testId={`flat-suggest-${r.eventId}`} />
            : <span className="muted">{r.matchConfidence === 'ambiguous' ? 'ambiguous' : 'unmatched'}</span>
        ),
    },
    {
      key: 'actions', label: 'Actions', tdStyle: { maxWidth: 260 },
      sortValue: (r) => (r.actions.length ? r.actions.map(actionLabel) : pendingOpsOf(r)).sort().join(', '),
      // William, 22 Oct 2026: a case with nothing resolved yet names what approval will do, one quiet pill per
      // waiting press ("Opt-Out"). Resolved actions show as before; a row from an older server reads "-".
      render: (r) => {
        const queuedOps = r.actions.length ? [] : pendingOpsOf(r);
        if (r.actions.length) {
          return (
            <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {r.actions.map((a) => (
                <span key={a} className="pill">{actionLabel(a)}</span>
              ))}
            </span>
          );
        }
        if (queuedOps.length) {
          return (
            <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }} data-testid={`queued-ops-${r.eventId}`}>
              {queuedOps.map((w, i) => (
                <span key={`${w}-${i}`} className="pill pill-queued" title={STAGED_WORDING.queuedOpHelp}>{w}</span>
              ))}
            </span>
          );
        }
        return <span className="muted">-</span>;
      },
    },
    {
      key: 'instruction', label: 'Instruction', tdStyle: nowrap, sortValue: (r) => (r.instruction ? actionLabel(r.instruction) : ''),
      render: (r) => (r.instruction ? <span className="pill">{actionLabel(r.instruction)}</span> : <span className="muted">-</span>),
    },
    { key: 'stage', label: 'Stage', sortValue: (r) => stageIndex(r.status), render: (r) => <StageDots status={r.status} /> },
    {
      key: 'status', label: 'Status', sortValue: (r) => statusLabel(r.status),
      render: (r) => {
        const st = STATUS_META[r.status] ?? { label: r.status, pill: '' };
        // Wave 3 item 4: how many presses wait for approval on this case (the list row's
        // pendingCount). Shown only when some do, which is never while presses are not staged.
        const queued = pendingCountOf(r);
        return (
          <>
            <span className={`pill ${st.pill}`.trim()}>{st.label}</span>
            {queued > 0 && (
              <span className="pill pill-warning" title={STAGED_WORDING.rowPillHelp(queued)} style={{ marginLeft: 4 }} data-testid={`row-queued-${r.eventId}`}>
                {STAGED_WORDING.rowPill(queued)}
              </span>
            )}
            {r.followUp ? <span title="follow-up"> ⏰</span> : null}
            {r.hasNotes ? <span title="has notes"> ✎</span> : null}
          </>
        );
      },
    },
    {
      key: 'tat', label: 'TAT', tdClassName: 'small', tdStyle: nowrap, sortValue: tat,
      render: (r) => {
        // Frozen once the server projected tatMinutes (sent to review OR approved); live otherwise.
        const mins = tat(r);
        const open = r.tatMinutes == null;
        return (
          <span title={open ? 'open: receipt to now' : 'receipt to sent-to-review'}>
            {mins === null ? <span className="muted">-</span> : <span className={open ? 'muted' : undefined}>{fmtMinutes(mins)}</span>}
          </span>
        );
      },
    },
    {
      key: 'handledBy', label: 'Handled by', tdClassName: 'small', tdStyle: nowrap, sortValue: (r) => r.operatorName || r.assignedTo,
      render: (r) => r.operatorName || r.assignedTo || <span className="muted">-</span>,
    },
  ];
  return o.rowsLook ? rowsLookColumns(cols, o) : cols;
}

/**
 * THE FLAT VIEW DRAWN LIKE GROUP BY MEMBER (William, 16 Oct 2026 8:20 PM, verbatim: "Make sure the 'flat
 * events' screen looks the same as the group by member"). The grouped view's columns, in its order, where
 * an entry has the data: a tick box with a header box, the row number, the member on two lines, Client,
 * Campaign, Cycle, Cohort, Text reply, Contact; no Offer. Then what only one entry has and an operator
 * needs per entry: its Channel, its Content and its Source; the Suggested action; its Date / Time where
 * the grouped view has Last activity; then Instruction, Stage, Status, TAT and Handled by as before.
 * Every key that existed keeps its name ('from' is the Contact column), so a remembered width and place
 * still apply. Left out: Offer (his word, the same as the grouped view), and the grouped view's open
 * marker and Events count (an entry is one event and has nothing to expand).
 */
function rowsLookColumns(base: GridColumn<EventRow>[], o: ColumnOpts): GridColumn<EventRow>[] {
  const by = new Map(base.map((c) => [c.key, c]));
  const col = (k: string): GridColumn<EventRow> => by.get(k)!;
  const header = o.headerIds ?? [];
  const allShown = header.length > 0 && header.every((id) => o.checkedIds?.has(id));
  return [
    tickColumn<EventRow>({
      on: !!o.onToggleAll,
      allShown,
      onToggleAll: () => o.onToggleAll?.([...header]),
      isChecked: (r) => o.checkedIds?.has(r.eventId) ?? false,
      onToggle: (r) => o.onToggleCheck?.(r.eventId),
      ariaLabel: (r) => `Select event ${r.eventId}`,
      testId: (r) => `flat-tick-${r.eventId}`,
      blocked: o.tickBlock,
    }),
    rowNoColumn<EventRow>(),
    {
      key: 'member', label: 'Member / Contact', tdStyle: nowrap, sortValue: (r) => eventMainLine(r),
      // The name and the id (or what an unmatched entry keyed on); the suggestion pill moved to Match (17 Oct 2026).
      render: (r) => {
        const who = eventWho(r);
        return <TwoLineCell main={threadMainLine(who)} sub={threadSubLine(who)} subTestId={`flat-sub-${r.eventId}`} />;
      },
    },
    {
      // William, 17 Oct 2026 about 12:50 AM: "the matched flag in its own col. sortable".
      key: 'match', label: MATCH_GROUP_WORDING.column, tdStyle: nowrap, sortValue: (r) => matchSortValue(eventMatchState(r)),
      render: (r) => <MatchCell state={eventMatchState(r)} testId={`flat-match-${r.eventId}`} pillTestId={`flat-suggest-${r.eventId}`} />,
    },
    {
      ...col('client'), label: 'Client', sortValue: (r) => (r.client ? clientDisplayName(r.client) : ''),
      render: (r) => (r.client ? clientDisplayName(r.client) : <span className="muted">-</span>),
    },
    col('campaign'),
    col('cycle'),
    { ...col('cohort'), render: (r) => r.replyCohort || <span className="muted">-</span> },
    {
      key: 'reply', label: 'Text reply', tdStyle: nowrap,
      sortValue: (r) => (r.replyClass ? REPLY_CLASS_META[r.replyClass]?.label ?? r.replyClass : ''),
      title: "The platform's reading of this text reply",
      render: (r) => {
        const marks = o.rowMarks?.(r) ?? [];
        if (!r.replyClass && !marks.length) return <span className="muted">-</span>;
        return (
          <>
            {r.replyClass && (
              <span className={`pill ${REPLY_CLASS_META[r.replyClass].pill}`} title={REPLY_CLASS_META[r.replyClass].help}>
                {REPLY_CLASS_META[r.replyClass].label}
              </span>
            )}
            {marks.map((m) => (
              <span key={m.key} className={`pill ${m.pill}`} title={m.title} style={{ marginLeft: 4 }} data-testid={`row-mark-${m.key}`}>
                {m.text}
              </span>
            ))}
          </>
        );
      },
    },
    {
      // Key kept as 'from' so an operator's remembered width and place still apply; the heading is Contact.
      ...col('from'), label: 'Contact',
      sortValue: (r) => { const c = eventContact(r); return c.phone || c.email; },
      // No Call link here: the rows look is the Inbound queue's, which stays quiet (the drawer offers the
      // call on row-open), so the table keeps its one guarded Call site, the From column above.
      render: (r) => {
        const c = eventContact(r);
        return <ContactCell phone={c.phone} email={c.email} />;
      },
    },
    col('channel'),
    col('content'),
    {
      // The entry's origin and its badges; the reply pill and its marks sit in Text reply, and
      // RE-SUBSCRIBED beside the suggestion, as in the grouped view.
      ...col('source'),
      render: (r) => {
        const src = SOURCE_META[r.source] ?? SOURCE_META.member;
        return (
          <>
            <span className={`pill ${src.pill}`.trim()} title={r.referralReason ? `referral: ${r.referralReason}` : undefined}>
              {src.label}
            </span>
            {r.optedOut && (
              <span
                className="pill pill-accent"
                title="The permanent opt-out record has this member suppressed. Open the Opt-outs screen to review the communication."
                style={{ marginLeft: 4 }}
              >
                {OPTED_OUT_WORDING.pill}
              </span>
            )}
            {r.claireResolved && (
              <span className="pill pill-success" title="Claire finished intake and auto-routed this to review" style={{ marginLeft: 4 }}>
                Claire Auto Resolved
              </span>
            )}
            {r.internal && (
              <span
                className="pill pill-warning"
                title="This number is on the internal-numbers roster (CDX team / test device). It is never matched to a member."
                style={{ marginLeft: 4 }}
              >
                CDX internal
              </span>
            )}
          </>
        );
      },
    },
    {
      key: 'suggested', label: 'Suggested action', tdStyle: nowrap,
      sortValue: (r) => (r.suggestedAction ? SUGGESTED_META[r.suggestedAction]?.label ?? r.suggestedAction : ''),
      render: (r) => <SuggestedCell r={r} stopWords="this STOP" />,
    },
    col('date'),
    col('actions'),
    col('instruction'),
    col('stage'),
    col('status'),
    col('tat'),
    // 16 Oct 2026 8:52 PM: Claimed by (the holder), in place of Handled by, same key.
    claimedByColumn<EventRow>((r) => r),
  ];
}

export interface QueueGridConfig {
  /** The screen's own name for its remembered layout ("inbound-flat", "calling", ...). */
  table: string;
  /** Every row the page holds for this view (the grid sorts and slices the WHOLE list). */
  items: EventRow[];
  /** The screen's own order; [] keeps the server's order (newest first). */
  defaultSort?: GridSortKey[];
  slicers?: QueueSlicer[];
  /** Row 354: rows with no campaign are never hidden by a slicer (a member started them). */
  protectNoCampaign?: boolean;
  /** Render a leading checkbox column (Final Review batch selection). */
  selectable?: boolean;
  checkedIds?: ReadonlySet<string>;
  onToggleCheck?: (id: string) => void;
  /** Approval 187: a disabled tick and its reason ('' = tickable). Keep it stable (useCallback). */
  tickBlock?: (r: EventRow) => string;
  /** Approval 187: marks beside the reply pill. Keep it stable (useCallback). */
  rowMarks?: ColumnOpts['rowMarks'];
  /**
   * Render the Handled-by column (default true). The New tab hides it: claiming an event moves it
   * to In progress (event-claim), so a New row NEVER has a handler - the column was always '-'
   * there (William 2026-07-06). In progress and later tabs show who is on it right now.
   */
  showHandledBy?: boolean;
  /** Replace the single-instruction column with an all-actions pill column (Final Review, item 12). */
  showActions?: boolean;
  /**
   * Approval 185: the Offer and Cohort columns (the Inbound queue with the reply buttons on). Off, both
   * are left out of the table AND the Columns list, so every other queue, and the Inbound queue with
   * the switch off, is exactly as before.
   */
  kitColumns?: boolean;
  /**
   * Render the From phone with its Teams click-to-call + copy actions (ergonomics 2026-07-11).
   * On for the out-calling worklist, where the row's whole point is placing that call; off elsewhere
   * so the inbound queues stay quiet (the drawer offers the same actions on row-open).
   */
  callable?: boolean;
  /**
   * William, 16 Oct 2026 8:20 PM: "Make sure the 'flat events' screen looks the same as the group by
   * member". The Inbound queue's flat view only: the grouped view's columns and order (rowsLookColumns),
   * its table class and its banded rows. Every other queue leaves it off and is exactly as before.
   */
  rowsLook?: boolean;
  /** Rows look: which rows on screen the header box takes (default every tickable row). Keep it stable. */
  headerTakes?: (r: EventRow) => boolean;
  /** Rows look: the header box's press, given the rows it takes. Absent = no header box. Keep it stable. */
  onToggleAll?: (ids: string[]) => void;
}

export interface QueueGrid {
  view: GridView<EventRow>;
  /** The rows on screen, in screen order: sorted and sliced. */
  rows: EventRow[];
  hidden: number;
  keptByRule: number;
  facets: Record<string, Facet[]>;
  protect: ((r: EventRow) => boolean) | undefined;
  /** The rows drawn like Group by member (QueueTable reads it for the table class and the bands). */
  rowsLook: boolean;
}

const noCampaign = (r: EventRow) => !r.campaign;

export function useQueueGrid(cfg: QueueGridConfig): QueueGrid {
  // Tick the open-row TAT once a minute so "live" rows advance between queue polls.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  const [headerIds, setHeaderIds] = useState<readonly string[]>([]);
  const columns = useMemo(
    () => queueColumns({
      nowMs, callable: !!cfg.callable, checkedIds: cfg.checkedIds, onToggleCheck: cfg.onToggleCheck,
      tickBlock: cfg.tickBlock, rowMarks: cfg.rowMarks,
      rowsLook: !!cfg.rowsLook, headerIds, onToggleAll: cfg.onToggleAll,
    }),
    [nowMs, cfg.callable, cfg.checkedIds, cfg.onToggleCheck, cfg.tickBlock, cfg.rowMarks, cfg.rowsLook, headerIds, cfg.onToggleAll],
  );
  const slicerKeys = (cfg.slicers ?? []).join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const slicers = useMemo(() => (cfg.slicers ?? []).map((k) => SLICER_DEFS[k]), [slicerKeys]);
  const omit = [
    ...(cfg.selectable ? [] : ['select']),
    cfg.showActions ? 'instruction' : 'actions',
    ...(cfg.showHandledBy === false && !cfg.rowsLook ? ['handledBy'] : []),
    ...(cfg.kitColumns ? [] : ['offer', 'cohort', 'cycle']),
  ];
  const view = useGridView(cfg.table, columns, { defaultSort: cfg.defaultSort ?? [], slicers, omit });
  const protect = cfg.protectNoCampaign ? noCampaign : undefined;
  const shown = useGridRows(view, cfg.items, protect);
  // Rows look: the rows on screen the header box ticks. Kept as state because the columns are built
  // before the view that decides which rows show (the grouped view does the same).
  const takes = cfg.headerTakes;
  const block = cfg.tickBlock;
  const headerJoined = cfg.rowsLook
    ? shown.rows.filter((r) => !block?.(r) && (takes ? takes(r) : true)).map((r) => r.eventId).join('\n')
    : '';
  useEffect(() => {
    setHeaderIds((prev) => (prev.join('\n') === headerJoined ? prev : headerJoined ? headerJoined.split('\n') : []));
  }, [headerJoined]);
  return { view, rows: shown.rows, hidden: shown.hidden, keptByRule: shown.keptByRule, facets: shown.facets, protect, rowsLook: !!cfg.rowsLook };
}

export interface QueueTableProps {
  grid: QueueGrid;
  selectedId: string | null;
  loading: boolean;
  onSelect: (id: string) => void;
  emptyLabel?: string;
  /** What a row is called in the slicers' hidden line. */
  noun?: string;
  /** Where the slicers are drawn (the Inbound queue's right-side rail); absent = over the table. */
  slicerHost?: HTMLElement | null | undefined;
}

export function QueueTable({ grid, selectedId, loading, onSelect, emptyLabel = 'No events.', noun = 'events', slicerHost }: QueueTableProps) {
  return (
    <HeldHeight hold={loading}>
      <CareGrid
        view={grid.view}
        rows={grid.rows}
        rowKey={(r) => r.eventId}
        noun={noun}
        server={{ facets: grid.facets, hidden: grid.hidden, keptByRule: grid.keptByRule }}
        protectWhat="Contacts with no campaign"
        tableWrapClassName="card card-flush"
        {...(grid.rowsLook ? { className: ROWS_TABLE_CLASS } : {})}
        // William, 16 Oct 2026 8:30 PM: the message's words under each row, as in the grouped view.
        line2={grid.rowsLook ? { lead: LINE2_LEAD, render: (r) => <MessageLine r={r} /> } : undefined}
        empty={loading ? 'Loading...' : emptyLabel}
        slicerHost={slicerHost}
        rowProps={(r, i) => {
          const st = STATUS_META[r.status] ?? { label: r.status, pill: '' };
          const selected = selectedId === r.eventId;
          return {
            tabIndex: 0,
            role: 'button',
            'aria-label':
              `Open event from ${r.fromPhone || r.fromAddress || 'unknown'}, ${st.label}`
              // The third carrier of the same fact, for an operator who sees neither the shade
              // nor the pill (rows 144/145).
              + (r.optedOut ? `, ${OPTED_OUT_WORDING.aria('')}` : ''),
            onClick: () => onSelect(r.eventId),
            onKeyDown: (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(r.eventId);
              }
            },
            style: {
              cursor: 'pointer',
              // Rows look (16 Oct 2026): alternate rows banded, as in the grouped view; the shade and the
              // selection below still win.
              ...(grid.rowsLook ? { background: rowBand(i) } : {}),
              // A SELECTED row keeps its selection colour: the operator's own focus must not be
              // hidden by the shade, and the pill and the aria-label still carry the fact.
              ...(r.optedOut && !selected ? OPTED_OUT_ROW_STYLE : {}),
              ...(selected ? { background: 'var(--paper-2)' } : {}),
            },
          };
        }}
      />
    </HeldHeight>
  );
}
